import React, { useState, useEffect } from 'react';
import {
  Stack,
  Typography,
  Chip,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Box,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import listingsService from '../../../../api/services/listings';

interface Feedback {
  pros: string[];
  cons: string[];
  feedback: string;
  overallFeedback: 'LIKE' | 'DISLIKE' | null;
}

interface PropertyFeedbackProps {
  onSubmit: (feedback: Feedback) => void;
  initialValue?: Feedback;
  listingId: string;
}

const PROS_OPTIONS = [
  'Great location',
  'Spacious',
  'Modern kitchen',
  'Quiet street',
  'Good natural light',
  'Move-in ready',
  'Nice yard',
];

const CONS_OPTIONS = [
  'Needs repairs',
  'Small yard',
  'Busy road',
  'Outdated appliances',
  'High HOA fees',
  'No parking',
  'Low natural light',
];

const PropertyFeedback: React.FC<PropertyFeedbackProps> = ({
  onSubmit,
  initialValue,
  listingId,
}) => {
  const [pros, setPros] = useState<string[]>(initialValue?.pros || []);
  const [cons, setCons] = useState<string[]>(initialValue?.cons || []);
  const [feedback, setFeedback] = useState<string>(
    initialValue?.feedback || ''
  );
  const [overallFeedback, setOverallFeedback] = useState<
    'LIKE' | 'DISLIKE' | null
  >(initialValue?.overallFeedback || null);

  useEffect(() => {
    if (initialValue) {
      setPros(initialValue.pros || []);
      setCons(initialValue.cons || []);
      setFeedback(initialValue.feedback || '');
      setOverallFeedback(initialValue.overallFeedback || null);
    }
  }, [initialValue]);

  const handleProsChange = (option: string) => {
    setPros((prev) =>
      prev.includes(option)
        ? prev.filter((p) => p !== option)
        : [...prev, option]
    );
  };

  const handleConsChange = (option: string) => {
    setCons((prev) =>
      prev.includes(option)
        ? prev.filter((c) => c !== option)
        : [...prev, option]
    );
  };

  const handleSubmit = async () => {
    try {
      const feedbackData = {
        pros,
        cons,
        feedback,
        overallFeedback,
      };

      await listingsService.patch({
        id: listingId,
        data: feedbackData as any,
      });
      onSubmit({ pros, cons, feedback, overallFeedback });
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  };

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <Stack spacing={2}>
        <Typography variant="h6">
          {initialValue ? 'Edit Tour Feedback' : 'Tour Feedback'}
        </Typography>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            What did you like?
          </Typography>
          {PROS_OPTIONS.map((option) => (
            <Chip
              key={option}
              label={option}
              color={pros.includes(option) ? 'success' : 'default'}
              onClick={() => handleProsChange(option)}
              sx={{ m: 0.5 }}
              variant={pros.includes(option) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            What didn&apos;t you like?
          </Typography>
          {CONS_OPTIONS.map((option) => (
            <Chip
              key={option}
              label={option}
              color={cons.includes(option) ? 'error' : 'default'}
              onClick={() => handleConsChange(option)}
              sx={{ m: 0.5 }}
              variant={cons.includes(option) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
        <TextField
          label="Additional notes"
          multiline
          minRows={2}
          maxRows={4}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          fullWidth
        />
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Overall impression
          </Typography>
          <ToggleButtonGroup
            value={overallFeedback}
            exclusive
            onChange={(_e, value) => setOverallFeedback(value)}
            aria-label="overall like or dislike"
            fullWidth
            sx={{ width: '100%' }}
          >
            <ToggleButton value="LIKE" aria-label="like" sx={{ flex: 1 }}>
              <ThumbUpIcon
                color={overallFeedback === 'LIKE' ? 'success' : 'disabled'}
              />
              <Typography sx={{ ml: 1 }}>Like</Typography>
            </ToggleButton>
            <ToggleButton value="DISLIKE" aria-label="dislike" sx={{ flex: 1 }}>
              <ThumbDownIcon
                color={overallFeedback === 'DISLIKE' ? 'error' : 'disabled'}
              />
              <Typography sx={{ ml: 1 }}>Dislike</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!overallFeedback}
        >
          {initialValue ? 'Update Feedback' : 'Submit Feedback'}
        </Button>
      </Stack>
    </Box>
  );
};

export default PropertyFeedback;
